import React, { useEffect, useState } from "react";

import orb from "../../../assets/images/orbit-orb.webp";

/**
 * The Orbit sign-in scene — the "Orbit · Sign in" handoff of 2026-09-23
 * (orbit-signin-react_2): wordmark, the globe inside three orbit rings, four
 * product cards, and the sign-in column the caller passes in.
 *
 * PRESENTATION ONLY. Nothing here knows about the login: Login.js owns every
 * step, field and request and hands its column in as `children`.
 *
 * THE PAGE IS THE ARTBOARD. The previous build was a fixed 1512 x 982
 * composition scaled with transform, which on a 1920 or 2560 monitor read as a
 * small rounded card floating in grey. Now the wash paints the whole viewport
 * and every measurement inside is a SHARE of its column: `.orbit-art` and
 * `.orbit-panel` declare `container-type: inline-size`, so 1cqw is 1% of that
 * column. Viewport units were the bug the handoff calls out — at 1366 the form
 * column is narrow while the viewport is not, so a vw-sized subheading wrapped.
 *
 * THE ENTRANCE, once per page load. Everything reveals now, left to right, the
 * way the finished page reads: mark, globe, rings, the four cards, then the
 * column one piece at a time. Each piece carries `.rv` plus a direction class
 * and its own delay; the stage gets `.go` one frame after mount. A step change
 * remounts pieces under an already-`.go` stage, which draws them at rest
 * without a transition.
 */

/** The art box's own coordinate system — the orb and cards are placed in it. */
export const ART = { w: 740, h: 560 };

/* Every delay and duration of the entrance, in ms — the handoff's timeline.js.
   Change a number here and the screen follows; nothing is hard-coded in CSS. */
export const T = {
  mark: { d: 80, dur: 620 },
  orb: { d: 180, dur: 980 },
  rings: { d: 420, dur: 900 },
  card0: { d: 700, dur: 620 },
  card1: { d: 820, dur: 620 },
  card2: { d: 940, dur: 620 },
  card3: { d: 1060, dur: 620 },
  headline1: { d: 1180, dur: 860 },
  headline2: { d: 1340, dur: 860 },
  rule: { d: 1560, dur: 720 },
  subheading: { d: 1720, dur: 780 },
  field: { d: 1920, dur: 800 },
  cta: { d: 2120, dur: 820 },
};

const CARD_T = [T.card0, T.card1, T.card2, T.card3];

/** Inline style for one revealed piece: `style={rv(T.rule)}`. */
export const rv = ({ d, dur }, extra = 0) => ({ "--d": `${d + extra}ms`, "--dur": `${dur}ms` });

/* Three rings, as fractions of the art box, centred on the globe (356, 268).
   `tilt` is the rotateX that makes a circle read as an orbit; `rot` is the
   in-plane rotation, which travels as a CUSTOM PROPERTY rather than an inline
   transform — an inline transform would beat `.rv-ring`'s
   `scale(.72) rotate(var(--r))` and the ring would fade in without swelling. */
const RINGS = [
  { d: 540 / ART.w, tilt: 70, rot: -16 },
  { d: 620 / ART.w, tilt: 76, rot: 28 },
  { d: 700 / ART.w, tilt: 64, rot: -44 },
];
const RING_CX = 356 / ART.w;
const RING_CY = 268 / ART.h;

/** The globe's box inside the art box. */
const ORB = { x: 106, y: 0, w: 500 };

/* ONE CARD SPEC drives all four. The Figma frame builds them at four different
   widths (256.83 / 239.19 / 273.95 / 251.06) with a 38.4 tile on Outcomes
   against 46.4 on the rest, which is why they looked mismatched; the handoff
   corrects that and says the file should be brought up to this, not the
   reverse. The text column works out to 274 − 12.76 − 17.4 − 46.4 − 12.76 =
   184.7px, between the tightest two-line fit (163.5) and the width at which
   the shortest title collapses to one line (229) — so every title is two
   lines at every size. */
export const CARD = {
  w: 274,
  radius: 8,
  stroke: 1.16,
  pad: [12.76, 17.4, 12.76, 12.76],
  gap: 12.76,
  tile: 46.4,
  tileR: 15.08,
  glyph: 23.2,
  ls: -0.19,
};

const CARDS = [
  { id: "outcomes", icon: "bar_chart", x: 10, y: 83, title: "Access longitudinal records and trends instantly" },
  { id: "iris", icon: "restaurant", x: 460, y: 109, title: "Generate personalized nutrition plans with IRIS" },
  { id: "echo", icon: "auto_awesome", x: 0, y: 318, title: "Advanced AI call intelligence with Echo" },
  { id: "appointments", icon: "calendar_today", x: 367, y: 361, title: "Manage your appointments and calendar efficiently" },
];

/** A length in art-box units, as a share of the box. */
const pc = (v) => `${((v / ART.w) * 100).toFixed(3)}cqw`;
/* CLAMPED, not free-scaling. Below its floor the BOX would keep shrinking while
   the TYPE had already stopped, which starves the text column and tips a card
   to three lines — the failure the handoff hit at 1366 before the floors. */
const cl = (min, v, max) => `clamp(${min}px, ${pc(v)}, ${max}px)`;

const MessageCard = ({ card, index }) => {
  const [pt, pr, pb, pl] = CARD.pad;
  return (
    <div
      className="orbit-card rv rv-card"
      style={{
        ...rv(CARD_T[index] || CARD_T[0]),
        left: `${(card.x / ART.w) * 100}%`,
        top: `${(card.y / ART.h) * 100}%`,
        width: cl(252, CARD.w, 384),
        padding: `${cl(11, pt, 18)} ${cl(15, pr, 24)} ${cl(11, pb, 18)} ${cl(11, pl, 18)}`,
        gap: cl(11, CARD.gap, 18),
        borderWidth: cl(1, CARD.stroke, 1.6),
        borderRadius: cl(8, CARD.radius, 12),
      }}
    >
      <span
        className="orbit-tile"
        style={{
          width: cl(40, CARD.tile, 64),
          height: cl(40, CARD.tile, 64),
          borderRadius: cl(13, CARD.tileR, 21),
        }}
      >
        <span className="orbit-glyph" style={{ fontSize: cl(20, CARD.glyph, 32) }}>
          {card.icon}
        </span>
      </span>
      <span className="orbit-card-title" style={{ letterSpacing: CARD.ls }}>
        {card.title}
      </span>
    </div>
  );
};

const OrbitStage = ({ toast, children }) => {
  const [go, setGo] = useState(false);

  /* One frame after the first paint, so the hidden starting styles are on
     screen before the transition to rest begins. */
  useEffect(() => {
    let second;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setGo(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  return (
    <div className="login-page orbit-login">
      {toast}
      {/* One frame holds the wordmark AND the two columns, so the mark's left
          edge is the edge the art starts on at every size — how the board is
          drawn. */}
      <div className={`orbit-stage${go ? " go" : ""}`}>
        <div className="orbit-mark rv rv-mark" style={rv(T.mark)} role="img" aria-label="Zyla">
          zyla
        </div>

        <div className="orbit-layout">
          <div className="orbit-art" aria-hidden="true">
            <span className="orbit-ground rv rv-orb" style={rv(T.orb)} />

            {RINGS.map((r, i) => (
              <div
                key={r.d}
                className="orbit-rw rv rv-ring"
                style={{
                  ...rv(T.rings, i * 90),
                  left: `${RING_CX * 100}%`,
                  top: `${RING_CY * 100}%`,
                  width: `${r.d * 100}%`,
                  aspectRatio: "1",
                  /* A percentage margin resolves against the containing block's
                     WIDTH on both axes, so both offsets are half the width. */
                  marginLeft: `${-r.d * 50}%`,
                  marginTop: `${-r.d * 50}%`,
                  "--r": `${r.rot}deg`,
                }}
              >
                <div className="orbit-ring" style={{ transform: `rotateX(${r.tilt}deg)` }} />
              </div>
            ))}

            {/* The globe's cast shadow, PAINTED rather than filtered: it used to
                be a drop-shadow filter on the image, and because the globe
                scales during the entrance Chromium re-rendered it every frame
                (37 of 153 frames over 33ms, against 1 of 190 for a gradient). */}
            <span
              className="orbit-orbcast rv rv-orb"
              style={{
                ...rv(T.orb),
                left: `${(91 / ART.w) * 100}%`,
                top: `${(30 / ART.h) * 100}%`,
                width: `${(530 / ART.w) * 100}%`,
                aspectRatio: "530 / 568",
              }}
            />

            <img
              className="orbit-orb rv rv-orb"
              style={{
                ...rv(T.orb),
                left: `${(ORB.x / ART.w) * 100}%`,
                top: `${(ORB.y / ART.h) * 100}%`,
                width: `${(ORB.w / ART.w) * 100}%`,
              }}
              src={orb}
              alt=""
            />

            {CARDS.map((c, i) => (
              <MessageCard key={c.id} card={c} index={i} />
            ))}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
};

export default OrbitStage;
